import './ProjectView.css';
import * as api from '../../api';
import inara16 from '../../assets/inara-16x16.png';
import { ActionButton, Callout, CommandBar, ContextualMenu, DefaultButton, DirectionalHint, Dropdown, DropdownMenuItemType, ICommandBarItemProps, Icon, IconButton, IContextualMenuItem, IDropdownOption, Label, Link, mergeStyles, MessageBar, MessageBarButton, MessageBarType, Modal, Panel, PanelType, PrimaryButton, Spinner, SpinnerSize, Stack, TeachingBubble, TextField } from '@fluentui/react';
import { Component, CSSProperties } from 'react';
import { BuildTypeDisplay, CargoRemaining, ChartByCmdrs, ChartByCmdrsOverTime, ChartGeneralProgress, CommodityIcon, EditCargo, FindFC } from '../../components';
import { store } from '../../local-storage';
import { appTheme, cn } from '../../theme';
import { autoUpdateFrequency, autoUpdateStopDuration, Cargo, CmdrShip, fc_loading, mapCommodityNames, mapShipNames, Project, ProjectFC, SortMode, SupplyStatsSummary } from '../../types';
import { delay, delayFocus, fcFullName, flattenObj, getCargoCountOnHand, getColorTable, getGroupedCommodities, getRelativeDuration, iconForSort, isMatchingCmdr, isMobile, mergeCargo, nextSort, openDiscordLink, sumCargo } from '../../util';
import { CopyButton } from '../../components/CopyButton';
import { FleetCarrier } from '../FleetCarrier';
import { LinkSrvSurvey } from '../../components/LinkSrvSurvey';
import { TimeRemaining } from '../../components/TimeRemaining';
import { EditProject } from '../../components/EditProject/EditProject';
import { MarketLinks } from '../../components/MarketLinks/MarketLinks';
import { BuildEffects } from '../../components/BuildEffects';
import { WhereToBuy } from '../../components/WhereToBuy/WhereToBuy';
import { getSiteType, mapName } from '../../site-data';
import { EconomyBlock } from '../../components/EconomyBlock';
import { ShowCoachingMarks } from '../../components/ShowCoachingMarks';
import { ViewEditBuildType } from '../SystemView2/ViewEditBuildType';
import { getAvgHaulCosts } from '../../avg-haul-costs';
import { EconomyTable2 } from '../SystemView2/EconomyTable2';
import { buildSystemModel2, SysMap2 } from '../../system-model2';
import { App } from '../../App';
import { InlineMarketOrders } from '../../components/InlineMarketOrders';

const css = mergeStyles({
  // TODO: ...
});

interface ProjectViewProps {
  buildId?: string;
}

interface ProjectViewState {
  buildId?: string;
  proj?: Project;
  sysMap?: SysMap2;
  lastTimestamp?: string;
  autoUpdateUntil: number;
  mode: Mode;
  sort: SortMode;
  loading: boolean;
  primaryBuildId?: string;
  refreshing?: boolean;
  showAddCmdr: boolean;
  newCmdr?: string;
  confirmDelete?: boolean;
  confirmComplete?: boolean;
  errorMsg?: string;

  assignCommodity?: string;
  assignCmdr?: string;
  hasAssignments?: boolean;

  editCommodities?: Cargo;
  editReady: Set<string>;
  editProject: boolean;
  disableDelete?: boolean;
  fixMarketId?: string;
  summary?: SupplyStatsSummary;
  sumTotal: number;

  showBubble: boolean;
  hideDoneRows: boolean;
  hideFCColumns: boolean;

  nextDelivery: Cargo;
  deliverMarketId: string;
  submitting: boolean;
  cargoContext?: string;

  showAddFC: boolean;
  savingFC?: boolean;

  fcCargo: Record<string, Cargo>;
  fcEditMarketId?: string;
  ships?: CmdrShip[];
  showShips?: boolean;
  showShipsTargetId?: string;
  showShipsTargetCargo?: string;

  showWhereToBuy?: boolean;

  notAgain: string[];
  notAgainPending?: string;

  // specific for prepation projects
  isPrep?: boolean;
  prepBuilds?: Record<string, number>;
  originalCargo?: Cargo;
  savingPrepBuilds?: boolean;
  prepBuildsDiffer?: boolean;
}

enum Mode {
  view,
  deliver,
}

export class ProjectView extends Component<ProjectViewProps, ProjectViewState> {
  private timer?: NodeJS.Timeout;
  /** The count of needed cargo already loaded on Fleet Carriers */
  private countReadyOnFCs: number = 0;

  constructor(props: ProjectViewProps) {
    super(props);

    const sortMode = store.commoditySort;
    this.state = {
      loading: true,
      autoUpdateUntil: Date.now() + autoUpdateStopDuration,
      primaryBuildId: App.cmdrSettings?.primaryBuildId,
      mode: Mode.view,
      sort: sortMode ?? SortMode.group,
      newCmdr: '',
      editProject: false,
      showAddCmdr: false,
      disableDelete: true,
      sumTotal: 0,

      editReady: new Set<string>(),
      hideDoneRows: store.commodityHideCompleted,
      hideFCColumns: store.commodityHideFCColumns,
      showBubble: !store.cmdr,
      nextDelivery: {},
      deliverMarketId: store.deliverDestination,
      submitting: false,

      showAddFC: false,
      fcCargo: {},

      notAgain: store.notAgain,
    };
  }

  componentDidMount() {
    // fetch initial data, which starts auto-updating as necessary
    if (this.props.buildId) {
      this.doNextPoll(this.props.buildId);
    }
  }

  componentWillUnmount(): void {
    if (this.timer) { clearTimeout(this.timer); }
  }

  componentDidUpdate(prevProps: Readonly<ProjectViewProps>, prevState: Readonly<ProjectViewState>, snapshot?: any): void {
    // buildId changed from external
    if (prevProps.buildId !== this.props.buildId && this.props.buildId) {
      if (this.timer) { clearTimeout(this.timer); }

      // set new buildId and reset the rest of state
      this.setState({
        buildId: this.props.buildId,
        proj: undefined,
        loading: true,
        autoUpdateUntil: Date.now() + autoUpdateStopDuration,
        primaryBuildId: App.cmdrSettings?.primaryBuildId,
        mode: Mode.view,
        newCmdr: '',
        editProject: false,
        showAddCmdr: false,
        disableDelete: true,
        sumTotal: 0,

        editReady: new Set<string>(),
        hideDoneRows: store.commodityHideCompleted,
        hideFCColumns: store.commodityHideFCColumns,
        showBubble: !store.cmdr,
        nextDelivery: {},
        deliverMarketId: store.deliverDestination,
        submitting: false,

        showAddFC: false,
        fcCargo: {},
      });
    }

    // buildId changed from above
    if (prevState.buildId !== this.state.buildId && this.state.buildId) {
      if (this.timer) { clearTimeout(this.timer); }
      if (!this.state.proj) {
        this.fetchProject(this.state.buildId, false);
      }
    }
  }

  getFullFCName(marketId: string) {
    const fc = this.state.proj?.linkedFC.find(fc => fc.marketId.toString() === marketId);
    if (fc)
      return fcFullName(fc.name, fc.displayName);
    else
      return '?';
  }

  async fetchProject(buildId: string | undefined, refreshing: boolean = false, polling: boolean = false) {
    if (!buildId) {
      this.setState({ loading: false });
      return;
    }

    try {
      clearTimeout(this.timer);
      this.setState({
        loading: !refreshing,
        refreshing: refreshing,
      });

      const newProj = await api.project.get(buildId);
      // console.log('Project.fetch: end buildId:', newProj);
      store.addRecentProject(newProj);

      // last destination not in this build project
      let deliverMarketId = this.state.deliverMarketId;
      if (!newProj.linkedFC.find(fc => fc.marketId.toString() === store.deliverDestination)) {
        deliverMarketId = 'site';
      }

      // shift end of auto-updating to be +1 hour from now
      const isPrep = newProj.buildType === fc_loading;
      let newAutoUpdateUntil = Date.now() + autoUpdateStopDuration;
      window.document.title = isPrep ? `Build: ${newProj.buildName}` : `Build: ${newProj.buildName} in ${newProj.systemName}`;
      if (newProj.complete) {
        window.document.title += ' (completed)';
        newAutoUpdateUntil = 0;
      }

      let ships: CmdrShip[] = [];
      let showShips = false;
      if (newProj.buildType !== fc_loading) {
        // (not awaiting)
        this.fetchProjectStats(buildId);
      }

      if (!newProj.complete) {
        if (Object.keys(newProj.linkedFC).length > 0) {
          this.fetchCargoFC(buildId); // (not awaiting)
        }

        ships = await api.project.getShips([buildId]);
        showShips = ships.some(ship => Object.keys(ship.cargo).some(c => ship.cargo[c] > 0 && newProj.commodities[c] > 0));
      }

      const prepBuildsDiffer = isPrep && newProj.prepBuilds && sumCargo(newProj.commodities) !== sumCargo(this.calcCargoNeedsForPrepBuilds(newProj.prepBuilds));

      this.setState({
        buildId: newProj.buildId,
        proj: newProj,
        originalCargo: { ...newProj.commodities },
        editProject: window.location.hash.startsWith('#edit'),
        editReady: new Set(newProj.ready),
        sumTotal: Object.values(newProj.commodities).reduce((total, current) => total += current, 0),
        hasAssignments: Object.keys(newProj.commanders).reduce((s, c) => s += newProj.commanders[c].length, 0) > 0,
        loading: false,
        refreshing: false,
        disableDelete: !!store.cmdrName && (!newProj.architectName || newProj.architectName.toLowerCase() !== store.cmdrName.toLowerCase()),
        deliverMarketId: deliverMarketId,
        autoUpdateUntil: newAutoUpdateUntil,
        ships,
        showShips,
        isPrep,
        prepBuilds: newProj.prepBuilds ?? {},
        prepBuildsDiffer,
      });

      if (newProj.complete) {
        const sys = await api.systemV2.getSys(newProj.systemAddress.toString());
        const sysMap = buildSystemModel2(sys, false, !!App.cmdrSettings?.noBuffNerf);
        this.setState({ sysMap });
      } else if (!isPrep) {
        // if ALL commodities have a count of -1 ... it means the project is brand new and we want people to edit them to real numbers
        if (Object.values(newProj.commodities).every(v => v === 10 || v === -1)) {
          this.setState({
            editCommodities: { ...newProj.commodities },
          });
          delayFocus('first-commodity-edit');
        }
      }

      // schedule next poll? (if project is incomplete)
      if (!newProj.complete && newAutoUpdateUntil > 0 && Date.now() < this.state.autoUpdateUntil) {
        //schedule next poll
        this.timer = setTimeout(() => this.doNextPoll(buildId), autoUpdateFrequency);
      }

      return newProj;

    } catch (err: any) {
      if (polling) {
        console.error(`Stop auto-updating at: ${new Date()} due to:\n`, err?.message);
        this.setState({ loading: false, refreshing: false, autoUpdateUntil: 0 });
      } else {
        this.setState({ loading: false, refreshing: false, errorMsg: err.message, });
      }
    }
  }

  doNextPoll = async (buildId: string) => {
    try {
      // skip polling when editing prepBuilds
      if (this.state.isPrep && this.state.prepBuilds && this.state.proj) {
        const prepBuildsDirty = JSON.stringify(this.state.prepBuilds) !== JSON.stringify(this.state.proj.prepBuilds ?? {});
        if (prepBuildsDirty) {
          // just schedule the next poll
          this.timer = setTimeout(() => this.doNextPoll(buildId), autoUpdateFrequency);
          console.debug(`skip polling when editing prepBuilds...`);
          return;
        }
      }

      // call server to see if anything changed
      const pollData = await api.project.poll([buildId, ...Object.keys(this.state.fcCargo)]);
      const timestamp = pollData.max;

      console.debug(`pollTimestamp at ${new Date().toISOString()}: changed? ${timestamp !== this.state.lastTimestamp} (${timestamp} vs ${this.state.lastTimestamp}) Will stop after: ${new Date(this.state.autoUpdateUntil).toISOString()}`);
      if (timestamp !== this.state.lastTimestamp) {
        // something has changed
        this.setState({ lastTimestamp: timestamp });
        await this.fetchProject(buildId, true, true);
      } else if (Date.now() < this.state.autoUpdateUntil) {
        // nothing changed, schedule next poll
        this.timer = setTimeout(() => this.doNextPoll(buildId), autoUpdateFrequency);
      } else {
        console.log(`Stopping auto-update after one hour of no changes at: ${new Date().toISOString()}`);
        this.setState({ autoUpdateUntil: 0 });
      }
    } catch (err: any) {
      console.error(`doNextPoll:`, err.stack);
      this.setState({ autoUpdateUntil: 0 });
    }
  };

  fetchProjectStats(buildId: string) {
    if (this.state.isPrep) { return; }

    api.project.getStats(buildId)
      .then(stats => this.setState({ summary: stats }))
      .catch(err => this.setState({ errorMsg: err.message }));
  }

  fetchCargoFC(buildId: string) {
    // don't bother calling when we know there are zero FCs or the project is complete
    if (this.state.proj && Object.keys(this.state.proj.linkedFC).length === 0) return;
    if (this.state.proj && this.state.proj.complete) return;

    api.project.getCargoFC(buildId)
      .then(fcCargo => this.setState({ fcCargo: fcCargo }))
      .catch(err => this.setState({ errorMsg: err.message }));
  }

  async setAsPrimary(buildId: string) {
    // add a little artificial delay so UI doesn't flicker
    this.setState({ refreshing: true })
    await delay(500);

    api.cmdr.setPrimary(store.cmdrName, buildId)
      .then(() => {
        store._primaryBuildId = buildId;
        App.cmdrSettings!.primaryBuildId = buildId;
        this.setState({ refreshing: false, primaryBuildId: buildId });
      })
      .catch(err => this.setState({ refreshing: false, errorMsg: err.message }));
  }

  async clearPrimary() {
    // add a little artificial delay so UI doesn't flicker
    this.setState({ refreshing: true })
    await delay(500);

    api.cmdr.clearPrimary(store.cmdrName)
      .then(() => {
        store._primaryBuildId = '';
        App.cmdrSettings!.primaryBuildId = '';
        this.setState({ refreshing: false, primaryBuildId: '' });
      })
      .catch(err => this.setState({ refreshing: false, errorMsg: err.message }));
  }

  render() {
    const { mode, proj, loading, refreshing, confirmDelete, confirmComplete, errorMsg, editProject, disableDelete, submitting, primaryBuildId, editCommodities, autoUpdateUntil, showWhereToBuy, fcCargo, isPrep } = this.state;

    if (loading) {
      return <div className='project-view'>
        <Spinner size={SpinnerSize.large} label={`Loading build project...`} />
      </div>;
    }

    if (!proj) {
      return <div className='project-view'>
        {errorMsg && <MessageBar messageBarType={MessageBarType.error}>{errorMsg}</MessageBar>}
      </div>;
    }

    // prep CommandBar buttons
    const hasDiscordLink = !!this.state.proj?.discordLink;
    let discordTitle = 'Edit the project to set a Discord link';
    if (hasDiscordLink)
      discordTitle = store.useNativeDiscord
        ? `Open link in Discord app:\n${this.state.proj?.discordLink}`
        : `Open Discord link in a tab:\n${this.state.proj?.discordLink}`;

    let refreshTitle = !!autoUpdateUntil ? 'Click to stop auto updating' : 'Click to refresh now and auto update every 30 seconds';
    if (proj.complete) refreshTitle = '';
    const commands: ICommandBarItemProps[] = [
      {
        className: cn.bBox,
        key: 'deliver-cargo',
        text: 'Deliver',
        title: (isPrep && !proj.linkedFC.length) ? 'Add Fleet Carriers to enable deliveries' : undefined,
        iconProps: { iconName: 'DeliveryTruck' },
        disabled: proj.complete || refreshing || (isPrep && !proj.linkedFC.length),
        style: { color: proj.complete || refreshing || (isPrep && !proj.linkedFC.length) ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => {
          const nextDelivery = { ...store.deliver };
          for (const k of Object.keys(nextDelivery)) {
            if (!proj.commodities[k]) {
              delete nextDelivery[k];
            }
          }
          this.setState({ mode: Mode.deliver, nextDelivery });
          delayFocus('deliver-commodity');
        },
      },
      {
        className: cn.bBox,
        key: 'btn-edit',
        text: 'Edit project',
        iconProps: { iconName: 'Edit' },
        disabled: refreshing,
        style: { color: refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => this.setState({ editProject: true }),
      },
      {
        className: cn.bBox,
        key: 'edit-commodities',
        text: 'Edit commodities',
        iconProps: { iconName: 'AllAppsMirrored' },
        disabled: proj.complete || refreshing,
        style: { color: proj.complete || refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => {
          this.setState({ editCommodities: { ...this.state.proj!.commodities } });
          delayFocus('first-commodity-edit');
        }
      },
      {
        className: cn.bBox,
        key: 'btn-refresh',
        text: 'Refresh',
        title: refreshTitle,
        iconProps: { iconName: !!autoUpdateUntil ? 'PlaybackRate1x' : 'Refresh' },
        disabled: refreshing,
        style: { color: refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => {
          this.toggleAutoRefresh();
        }
      },
      {
        className: cn.bBox,
        key: 'btn-delete', text: 'Delete',
        iconProps: { iconName: 'Delete' },
        disabled: disableDelete || refreshing,
        style: { color: disableDelete || refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => {
          this.setState({ confirmDelete: true });
          delayFocus('delete-no');
        },
      },
      {
        className: cn.bBox,
        key: 'btn-primary', text: 'Primary',
        iconProps: { iconName: proj.buildId === primaryBuildId ? 'SingleBookmarkSolid' : 'SingleBookmark' },
        title: proj.buildId === primaryBuildId ? 'This is your current primary project. Click to clear.' : 'Set this as your current primary project',
        disabled: proj.complete || refreshing,
        style: { color: proj.complete || refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        onClick: () => {
          if (proj.buildId === primaryBuildId)
            this.clearPrimary();
          else
            this.setAsPrimary(proj.buildId);
        },
      },
      {
        className: cn.bBox,
        key: 'discord-link',
        text: 'Discord',
        title: discordTitle,
        disabled: !hasDiscordLink || refreshing,
        style: { color: !hasDiscordLink || refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        iconProps: { iconName: 'OfficeChatSolid' },
        onClick: () => openDiscordLink(this.state.proj?.discordLink)
      },
      {
        className: cn.bBox,
        key: 'btn-open',
        iconProps: { iconName: 'OpenInNewWindow' },
        disabled: refreshing,
        style: { color: refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
        subMenuProps: {
          calloutProps: { style: { border: '1px solid ' + appTheme.palette.themePrimary } },
          items: [
            {
              className: cn.bBox,
              key: 'btn-open-inara',
              text: 'View on Inara',
              iconProps: { imageProps: { src: inara16 } },
              disabled: refreshing,
              style: { color: refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
              onClick: () => {
                window.open(`https://inara.cz/elite/station/?search=${encodeURIComponent(proj.buildName)} [${encodeURIComponent(proj.systemName)}]`, 'Inara');
              },
            },
            // {
            //   key: 'btn-open-spansh',
            //   text: 'View on Spansh',
            //   disabled: refreshing,
            //   style: { color: refreshing ? appTheme.palette.neutralTertiaryAlt : undefined },
            //   onClick: () => {
            //     window.open(`https://spansh.co.uk/station/${proj.marketId}`, 'Spansh');
            //   },
            // }
          ],
        },
      },
    ];

    if (proj.complete) {
      // remove buttons from completed projects: Deliver and Edit Commodities
      commands.splice(0, 1); // Deliver
      commands.splice(1, 1); // Edit commodities
      commands.splice(3, 1); // Set primary
    }

    // prepare rich copy link
    var copyLink = new ClipboardItem({
      'text/plain': `https://ravencolonial.com/#build=${proj.buildId}`,
      'text/html': new Blob([`<a href='${`https://ravencolonial.com/#build=${proj.buildId}`}'>${proj.buildName}</a>`], { type: 'text/html' }),
    });

    const fcMergedCargo = mergeCargo(Object.values(fcCargo))
    return <div className={`project-view ${css}`}>
      {errorMsg && <MessageBar messageBarType={MessageBarType.error}>{errorMsg}</MessageBar>}
      <div className='full'>
        <h2 className='project-title'>
          {isPrep && <>
            <Icon className='icon-inline' iconName='AddToShoppingList' style={{ color: appTheme.palette.themeTertiary, fontSize: 20, marginRight: 10 }} />
            {proj.buildName}
          </>}
          {!isPrep && <>
            <CopyButton text={proj.systemName} fontSize={16} />
            <Link href={`#sys=${encodeURIComponent(proj.systemName)}`} style={{ marginLeft: 4 }}>{proj.systemName}</Link>: {proj.buildName} {proj.complete && <span> (completed)</span>}
            <span style={{ fontSize: 16 }}><CopyButton text={copyLink} title='Copy a link to this page' /></span>
          </>}
        </h2>
        {proj.marketId <= 0 && this.renderMissingMarketId()}
        {mode === Mode.view && <CommandBar className={`top-bar ${cn.bb} ${cn.bt} ${cn.topBar}`} items={commands} />}
      </div >

      <div className='contain-horiz'>
        {!proj.complete && mode === Mode.view && <div className='half'>
          {this.renderCommodities(fcMergedCargo)}
        </div>}

        {!isPrep && mode === Mode.view && proj.complete && this.renderSystemEffects(proj)}

        <WhereToBuy
          visible={!!showWhereToBuy}
          buildIds={[this.state.buildId!]}
          systemName={this.state.proj!.systemName}
          need={this.state.proj!.commodities}
          have={this.state.hideFCColumns ? undefined : fcMergedCargo}
          onClose={() => this.setState({ showWhereToBuy: false })}
        />

        {!!editCommodities && !proj.complete && this.renderEditCommodities()}

        {mode === Mode.view && this.renderProjectDetails(proj)}

        {editProject && <EditProject proj={proj}
          onChange={savedProj => {
            if (window.location.hash.startsWith('#edit')) {
              window.location.hash = `#build=${proj.buildId}`;
            }

            if (!savedProj) {
              this.setState({ mode: Mode.view, editProject: false, });
              return;
            }

            const cmdrName = store.cmdrName;
            this.setState({
              proj: savedProj,
              originalCargo: { ...savedProj.commodities },
              editProject: false,
              lastTimestamp: savedProj.timestamp,
              editReady: new Set(savedProj.ready),
              sumTotal: sumCargo(savedProj.commodities),
              hasAssignments: Object.keys(savedProj.commanders).reduce((s, c) => s += savedProj.commanders[c].length, 0) > 0,
              disableDelete: !!cmdrName && !!savedProj.architectName && savedProj.architectName.toLowerCase() !== cmdrName.toLowerCase(),
              mode: Mode.view,
              submitting: false,
            });
          }}
        />}

        {mode === Mode.view && !!proj.maxNeed && this.renderStats()}
      </div>

      <ShowCoachingMarks id='whereToShop' target='#btnWhereToBuy' />

      <Modal
        isOpen={confirmDelete}
        onDismiss={() => this.setState({ confirmDelete: false })}
        styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}
      >
        <div className='center'>
          <p>
            <h3 className={cn.h3}>Are you sure you want to delete?</h3>
            <br />
            This cannot be undone.</p>
          <DefaultButton
            text='Yes'
            disabled={submitting}
            iconProps={{ iconName: 'Warning' }}
            style={{ backgroundColor: appTheme.palette.yellowDark, color: 'black' }}
            onClick={this.onProjectDelete}
          />
          &nbsp;
          <DefaultButton
            text='No'
            id='delete-no'
            iconProps={{ iconName: 'Cancel' }}
            disabled={submitting}
            onClick={() => this.setState({ confirmDelete: false })}
          />
        </div>
      </Modal>

      <Modal isOpen={confirmComplete} styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}>
        <div className='center'>
          <p>
            <h3 className={cn.h3}>Confirm completion?</h3>
            <br />
            Completed projects can still be found but cannot be made active again.
          </p>
          <PrimaryButton text='Yes' iconProps={{ iconName: 'Accept' }} onClick={this.onProjectComplete} disabled={submitting} />
          &nbsp;
          <DefaultButton text='No' iconProps={{ iconName: 'Cancel' }} onClick={() => this.setState({ confirmComplete: false })} disabled={submitting} />
        </div >
      </Modal>

      {mode === Mode.deliver && this.renderDeliver()}
    </div>;
  }

  renderEditCommodities() {
    const { proj, editCommodities, sort, submitting, isPrep } = this.state;
    const isDefaultCargo = Object.values(proj!.commodities).every(v => v === 10 || v === -1);

    return <Panel
      isOpen
      allowTouchBodyScroll={isMobile()}
      type={PanelType.custom}
      customWidth={'400px'}
      headerText='Edit commodities'
      onDismiss={() => this.setState({ mode: Mode.view, editCommodities: undefined })}
      styles={{
        overlay: { backgroundColor: appTheme.palette.blackTranslucent40 },
      }}

      isFooterAtBottom={!isMobile()}
      onRenderFooterContent={() => {
        return <>
          {submitting && <Spinner
            className='submitting'
            label="Updating commodities ..."
            labelPosition="right"
          />}

          {!submitting && <Stack horizontal horizontalAlign='end' tokens={{ childrenGap: 4, padding: 0, }} verticalAlign='end'>
            <PrimaryButton
              text='Save'
              iconProps={{ iconName: 'Save' }}
              onClick={this.onUpdateProjectCommodities}
            />
            <DefaultButton
              text='Cancel'
              iconProps={{ iconName: 'Cancel' }}
              onClick={() => {
                this.setState({ mode: Mode.view, editCommodities: undefined });
              }}
            />
          </Stack>}
        </>;
      }}
    >
      {isDefaultCargo && !isPrep && <MessageBar
        messageBarType={MessageBarType.warning}
        actions={<MessageBarButton onClick={this.setDefaultApproxCargoCounts}
        >
          Use approximate values
        </MessageBarButton>}
      >
        <div>Please manually enter required cargo numbers:</div>
        <div>Or dock at the construction site running<LinkSrvSurvey /></div>
        <div>Or proceed using approximate default values.</div>
      </MessageBar>}

      <EditCargo
        addButtonAbove={isPrep ? true : undefined} noAdd={!isPrep} noDelete={!isPrep} showTotalsRow
        cargo={editCommodities!}
        sort={sort}
        onChange={cargo => this.setState({ editCommodities: cargo })}
      />
    </Panel>;
  }

  renderCommodities(fcMergedCargo: Cargo) {
    const { proj, assignCommodity, sumTotal, mode, sort, hideDoneRows, hideFCColumns, hasAssignments, fcCargo, showWhereToBuy, showShips, showShipsTargetId, showShipsTargetCargo, isPrep } = this.state;
    if (!proj?.commodities) { return <div />; }

    // start with commodities from project, adding things on FCs (if they are visible)
    const cargo: Cargo = {
      ...proj.commodities,
      // always show tritium
      tritium: proj.commodities.tritium ?? 0,
    };
    if (!hideFCColumns) {
      for (const fcc of Object.values(fcCargo)) {
        for (const key in fcc) {
          if (key in cargo || fcc[key] === 0 || !mapCommodityNames[key]) { continue; }
          cargo[key] = 0;
        }
      }
    }

    // do not render list if nothing left to deliver
    if (sumTotal === 0) {
      if (isPrep) {
        return <>
          <div style={{ textAlign: 'center', minWidth: 500, marginTop: 40, fontSize: 14 }}>
            <ActionButton
              className={cn.bBox2}
              text='Add a building to track required cargo'
              onClick={() => {
                document.getElementById('bt-add-build-btn')?.click();
              }}
            />

            <div style={{ margin: 40, textAlign: 'center', color: appTheme.palette.themeSecondary }}>
              Loading projects are lighter weight, intended for collecting supplies on Fleet Carriers before placing construction sites.
              <br />
              <br />
              They can track cargo needed for multiple builds.
              <br />
              <br />
              <br />
              <div style={{ color: appTheme.palette.yellowDark, fontSize: 12, margin: '10px 0' }}>
                <Icon className='icon-inline' iconName='Warning' />
                &nbsp;This is an experimental feature - these projects may be hidden or unsupported in older versions of client apps
              </div>
            </div>
          </div>
        </>;
      } else {
        return <>
          <div>Congratulations!</div>
          <br />
          <PrimaryButton iconProps={{ iconName: 'Completed' }} onClick={() => this.setState({ confirmComplete: true })}>Mark project complete</PrimaryButton>
        </>;
      }
    }

    const rows = [];
    const cmdrs = proj.commanders ? Object.keys(proj.commanders) : [];

    let flip = false;
    const validCargoNames = Object.keys(cargo).filter(k => !hideDoneRows || cargo[k] !== 0 || proj.complete);
    const groupedCommodities = getGroupedCommodities(validCargoNames, sort);
    const groupsAndCommodityKeys = flattenObj(groupedCommodities);

    let colSpan = 0;
    const fcCount = Object.keys(fcCargo).length;
    if (fcCount > 0) colSpan += fcCount + 1;
    if (hasAssignments) colSpan++;
    if (showShips) colSpan++;

    // calculate sum cargo diff for all FCs per commodity name
    const fcMarketIds = Object.keys(fcCargo);
    let errorLogged = false;
    const mapSumCargoDiff = Object.keys(mapCommodityNames).reduce((map, key) => {
      if (key in cargo) {
        const need = cargo[key] ?? 0;
        if (need >= 0) {
          map[key] = fcMarketIds.reduce((sum, marketId) => {
            if (fcCargo[marketId] && fcCargo[marketId][key]) {
              sum += fcCargo[marketId][key];
            } else if (fcCargo[marketId] == null && !errorLogged) {
              console.error(`Why bad cargo for marketId: '${marketId}' on key: '${key}'?\n`, JSON.stringify(this.state, null, 2));
              errorLogged = true;
            }
            return sum;
          }, 0) - need;
        }
      }
      return map;
    }, {} as Cargo);

    // as we have the FC cargo diff's known here, calculate how much they have ready for the total progress chart. Meaning: count the needs where FCs have a surplus, otherwise count what is on the FC
    this.countReadyOnFCs = getCargoCountOnHand(cargo, fcMergedCargo);

    // calculate sum of diffs that are negative
    const fcSumCargoDeficit = Object.values(mapSumCargoDiff)
      .filter(v => v < 0)
      .reduce((sum, v) => sum += -v, 0);

    let countEnoughOnAnything = 0;
    for (const key of groupsAndCommodityKeys) {
      if (key in groupedCommodities) {
        if (sort === SortMode.alpha) { continue; }

        // group row
        let td = <td colSpan={2 + colSpan} className='hint'> {key}</td>;
        if (sort === SortMode.econ) {
          const txt = key.split(',').map(t => mapName[t] ?? '??').join(' / ');
          td = <td colSpan={2 + colSpan}>
            <Stack horizontal verticalAlign='center'>
              <div><EconomyBlock economy={key} /></div>
              <div className='hint'>{txt}</div>
            </Stack>
          </td>;
        }

        rows.push(<tr key={`group-${key}`} className='group' style={{ background: appTheme.palette.themeSecondary, color: appTheme.palette.neutralLight }}>{td}</tr>);
        continue;
      }

      // TODO: Entirely split view rendering
      flip = !flip;
      var { row, enoughOnShipsOrFC } = this.getCommodityRow(proj, key, cmdrs, flip, mapSumCargoDiff, fcCargo);
      rows.push(row)
      if (enoughOnShipsOrFC) { countEnoughOnAnything++; }

      // show extra row to assign a commodity to a cmdr?
      if (assignCommodity === key && proj.commanders) {
        rows.push(this.getCommodityAssignmentRow(key, proj, cmdrs));
      }
    }

    // generate a totals row at the bottom
    const totals: string[] = [sumTotal.toLocaleString()];
    if (showShips) { totals.push(''); }
    if (!hideFCColumns && Object.keys(fcCargo).length > 0) {
      totals.push('');
      for (const fcc of Object.values(fcCargo)) {
        totals.push(sumCargo(fcc).toLocaleString() ?? '');
      }
    }
    let nn = 0;
    const totalIsEnough = validCargoNames.length && countEnoughOnAnything === validCargoNames.length;
    const totalsRow = <tr key='tr-sum'>
      <td className={`commodity-name ${cn.br} ${cn.bt}`} style={{ textAlign: 'right' }}>
        <span>Sum total:&nbsp;</span>
        {totalIsEnough && <Icon iconName={'TaskSolid'} style={{ fontSize: 14, height: 16, width: 16, marginRight: 2, marginTop: 4, color: 'lime' }} />}
      </td>
      {totals.map((t, i) => (<td key={`tr-${++nn}`} className={`commodity-need ${i + 1 < totals.length || hasAssignments ? cn.br : ''} ${cn.bt}`}>{t}</td>))}
      {hasAssignments && <td className={cn.bt} style={{ textAlign: 'right' }}>&nbsp;</td>}
    </tr>;

    return <div style={{ cursor: 'default' }}>
      {mode === Mode.view && <h3 className={cn.h3} style={{ marginBottom: 16 }}>
        Commodities:&nbsp;
        <ActionButton
          className={cn.bBox}
          iconProps={{ iconName: iconForSort(sort) }}
          onClick={() => {
            const newSort = nextSort(sort);
            this.setState({ sort: newSort });
            store.commoditySort = newSort;
          }}
        >
          {sort}
        </ActionButton>
        <ActionButton
          className={cn.bBox}
          iconProps={{ iconName: hideDoneRows ? 'ThumbnailViewMirrored' : 'AllAppsMirrored' }}
          title={hideDoneRows ? 'Hiding completed commodies' : 'Showing all commodities'}
          text={hideDoneRows ? 'Active' : 'All'}
          onClick={() => {
            this.setState({ hideDoneRows: !hideDoneRows });
            store.commodityHideCompleted = !hideDoneRows;
          }}
        />
        {fcCount > 0 && <ActionButton
          className={cn.bBox}
          iconProps={{ iconName: hideFCColumns ? 'fleetCarrier' : 'fleetCarrierSolid' }}
          title={hideFCColumns ? 'Hiding FC columns' : 'Showing FC columns'}
          onClick={() => {
            this.setState({ hideFCColumns: !hideFCColumns });
            store.commodityHideFCColumns = !hideFCColumns;
          }}
        />}

        <ActionButton
          id='btnWhereToBuy'
          className={cn.bBox}
          iconProps={{ iconName: showWhereToBuy ? 'ShoppingCartSolid' : 'ShoppingCart' }}
          title='Find markets supplying required commodities'
          onClick={() => {
            this.setState({ showWhereToBuy: !showWhereToBuy });
          }}
        />
      </h3>}

      <table className={`commodities ${sort}`} cellSpacing={0} cellPadding={0} style={{ fontSize: 14 }} >
        <thead>
          <tr>
            <th className={`commodity-name ${cn.bb} ${cn.br}`}>Commodity</th>
            <th className={`commodity-need ${cn.bb} ${cn.br}`} title='Total needed for this commodity'>Need</th>
            {showShips && <th className={`commodity-need ${cn.bb} ${cn.br}`} title='Cargo on tracked ships'>
              <IconButton
                id='show-all-ships'
                iconProps={{ iconName: showShipsTargetId && !showShipsTargetCargo ? 'AirplaneSolid' : 'Airplane' }}
                className={`bubble ${cn.bBox}`}
                style={{ height: 20, padding: 0, }}
                onClick={() => this.setState({ showShipsTargetId: !!showShipsTargetId ? undefined : 'show-all-ships' })}
              />
            </th>}
            {!hideFCColumns && colSpan > 0 && Object.keys(fcCargo).length > 0 && this.getCargoFCHeaders()}
            {hasAssignments && <th className={`commodity-assigned ${cn.bb}`}>Assigned</th>}
          </tr>
        </thead>
        <tbody>
          {rows}
          {totalsRow}
        </tbody>
      </table>

      {sumTotal > 0 && <div className='cargo-remaining'>
        {!isPrep && <CargoRemaining sumTotal={sumTotal} label='Remaining cargo' />}
        {!hideFCColumns && fcSumCargoDeficit > 0 && <CargoRemaining sumTotal={fcSumCargoDeficit} label='Fleet Carrier deficit' />}
      </div>}

      {!!showShipsTargetId && this.renderShipsCallout()}
    </div>
  }

  getCargoFCHeaders() {
    const keys = Object.keys(this.state.fcCargo);
    return [
      <th key={`fcc-have`} className={`commodity-need ${cn.bb} ${cn.br}`} title='Difference between amount needed and sum total across linked Fleet Carriers'>FC Diff</th>,
      ...keys.map((k, i) => {
        const fc = this.state.proj!.linkedFC.find(fc => fc.marketId.toString() === k);
        return fc && <th key={`fcc${k}`} className={`commodity-need ${cn.bb} ${i + 1 < keys.length || this.state.hasAssignments ? cn.br : ''}`} title={`${fc.displayName} (${fc.name})`} >
          <ActionButton
            className={cn.bBox}
            style={{ height: 20, padding: 0, fontWeight: 'bold', color: appTheme.palette.themePrimary }}
            onClick={() => this.setState({ fcEditMarketId: k })}
          >
            {fc.name}
          </ActionButton>
        </th>;
      })
    ];
  }

  getCommodityAssignmentRow(key: string, proj: Project, cmdrs: string[]) {
    if (Object.keys(proj.commanders).length === 0) {
      // show a warning when there's no cmdrs to add
      return <tr key={`c${key}`}>
        <td colSpan={2}>
          <MessageBar messageBarType={MessageBarType.warning} onDismiss={() => this.setState({ assignCommodity: undefined })} >You need to add commanders first</MessageBar>
        </td>
      </tr>;
    } else {
      const cmdrOptions = cmdrs
        .filter(cmdr => proj.commanders && proj.commanders[cmdr] && !proj.commanders[cmdr].includes(key))
        .map(k => ({ key: k, text: k }))
        .sort();

      const assignRow = <tr key='c-edit'>
        <td colSpan={2}>
          <Stack horizontal>
            <Dropdown
              id='assign-cmdr'
              openOnKeyboardFocus
              placeholder='Assign a commander'
              options={cmdrOptions}
              selectedKey={this.state.assignCmdr}
              onChange={(_, o) => this.onClickAssign(`${o?.key}`)}
              onDismiss={() => this.setState({ assignCommodity: undefined })}
              styles={{ callout: { border: '1px solid ' + appTheme.palette.themePrimary } }}
            />
            <IconButton title='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => this.setState({ assignCommodity: undefined })} />
          </Stack>
        </td>
      </tr>;
      return assignRow;
    }
  }

  getCommodityRow(proj: Project, key: string, cmdrs: string[], flip: boolean, mapSumCargoDiff: Cargo, fcCargo: Record<string, Cargo>): { enoughOnShipsOrFC: boolean, row: JSX.Element } {
    const displayName = mapCommodityNames[key] ?? key;
    const currentCmdr = store.cmdrName;

    const assigned = cmdrs
      .filter(k => cmdrs.some(() => proj!.commanders && proj!.commanders[k].includes(key)))
      .map(k => {
        return <span className={`removable bubble ${cn.removable}`} key={`$${key}-${k}`} style={{ backgroundColor: k === currentCmdr ? appTheme.palette.themeLight : undefined }}>
          <span className={`glue ${k === currentCmdr ? 'active-cmdr' : ''}`} >📌{k}</span>
          <Icon
            className={`btn ${cn.btn}`}
            iconName='Delete'
            title={`Remove assignment of ${displayName} from ${k}`}
            style={{ color: appTheme.palette.themePrimary }}
            onClick={() => this.onClickUnassign(k, key)}
          />
        </span>;
      });

    const need = proj.commodities[key] ?? 0;


    const isContextTarget = this.state.cargoContext === key;
    const isReady = this.state.editReady.has(key);

    const menuItems: IContextualMenuItem[] = [
      {
        key: 'assign-cmdr',
        text: 'Assign to a commander ...',
        iconProps: { iconName: 'PeopleAdd' },
        onClick: () => {
          this.setState({ assignCommodity: key });
          delayFocus('assign-cmdr');
        },
      }
    ];

    if (need > 0 || isReady) {
      menuItems.push({
        key: 'toggle-ready',
        text: isReady ? 'Clear ready' : 'Mark ready',
        onClick: () => {
          this.onToggleReady(this.state.proj!.buildId, key, isReady);
        },
        iconProps: { iconName: 'StatusCircleCheckmark' },
      });
    }

    if (need > 0 && !this.state.isPrep) {
      menuItems.push({
        key: 'set-to-zero',
        text: 'Set to zero',
        onClick: () => this.deliverToZero(key, need),
        iconProps: { iconName: 'Download' },
      });
    }

    const sumCargoDiff = mapSumCargoDiff[key] ?? 0;
    let diffCargoFC = (sumCargoDiff).toLocaleString();
    if (!diffCargoFC.startsWith('-') && diffCargoFC !== '0') diffCargoFC = '+' + diffCargoFC;

    const fcTotal = sumCargoDiff + need;
    const diff = 100 / need * fcTotal;

    // prepare an element for the FC diff cell
    const fcSumTitle = sumCargoDiff > 0
      ? `${diff.toFixed(0)}% - FCs have a surplus of: ${sumCargoDiff}`
      : `${diff.toFixed(0)}% - FCs are short by: ${-sumCargoDiff}`;
    let fcSumElement = <></>;
    if (need > 0 && sumCargoDiff === 0) {
      fcSumElement = <Icon className='icon-inline' iconName='CheckMark' title={`FCs have enough ${displayName}`} style={{ cursor: 'Default', textAlign: 'center', width: '100%' }} />;
    } else if (need > 0 || sumCargoDiff > 0) {
      fcSumElement = <span title={fcSumTitle}>{diffCargoFC}</span>;
    }
    // prepare bubble colour for FC diff cell
    let fcDiffCellColor = '';
    if (need > 0) {
      fcDiffCellColor = sumCargoDiff >= 0 ? 'lime' : appTheme.palette.yellow;
    }

    const { ships, showShips } = this.state;
    const onShips = ships && ships.map(s => s.cargo[key] ?? 0);
    const countOnShips = onShips?.reduce((t, c) => t + c, 0) ?? 0;
    const onShipsElement = !countOnShips
      ? <span style={{ color: 'grey' }}>-</span>
      : <ActionButton
        id={`show-ships-${key}`}
        className={`bubble ${cn.bBox}`}
        style={{ height: 20, padding: 0, minWidth: 28, }}
        text={countOnShips.toLocaleString()}
        onClick={() => this.setState({ showShipsTargetCargo: key, showShipsTargetId: `show-ships-${key}` })}
      />;

    const enoughOnShipsOrFC = need > 0 && (countOnShips + sumCargoDiff >= 0);

    const fcMarketIds = Object.keys(fcCargo);

    const className = need !== 0 ? '' : 'done ';
    const style: CSSProperties | undefined = flip ? undefined : { background: appTheme.palette.themeLighter };
    return {
      enoughOnShipsOrFC: enoughOnShipsOrFC,
      row: <tr key={`cc-${key}`} className={className} style={style}>
        <td className={`commodity-name ${cn.br}`} id={`cargo-${key}`} style={{ position: 'relative' }}>
          <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 2 }}>
            <CommodityIcon name={key} /> <span id={`cn-${key}`} className='t'>{displayName}</span>
            &nbsp;
            {isReady && <Icon iconName='CompletedSolid' className='icon-inline' style={{ fontSize: 14 }} title={`${displayName} is ready`} />}

            {isContextTarget && <ContextualMenu
              target={`#cn-${key}`}
              onDismiss={() => this.setState({ cargoContext: undefined })}
              items={menuItems}
              styles={{
                container: { margin: -10, padding: 10, border: '1px solid ' + appTheme.palette.themePrimary, }
              }}
            />}

            <IconButton
              className={`btn ${cn.bBox}`}
              iconProps={{ iconName: 'ContextMenu' }}
              title={`Commands for: ${key}`}
              style={{ position: 'absolute', right: 20, top: 1, color: appTheme.palette.themePrimary, width: 22, height: 20 }}
              onClick={() => {
                this.setState({ cargoContext: key });
              }}
            />
          </Stack>

          {(enoughOnShipsOrFC) && <span style={{ position: 'absolute', right: 0, top: 2 }} title='Enough cargo is available on linked Fleet Carriers or tracked ships'>
            <Icon iconName={'TaskSolid'} style={{ fontSize: 14, height: 16, width: 16, color: 'lime' }} />
          </span>}

        </td>

        <td className={`commodity-need ${cn.br}`}>
          <span className='t'>{need === -1 ? '?' : need.toLocaleString()}</span>
        </td>

        {showShips && <td className={`${cn.br}`} style={{ textAlign: 'center' }}>{onShipsElement}</td>}

        {fcMarketIds.length > 0 && !this.state.hideFCColumns && <>
          {/* The FC Diff cell, then a cell for each FC */}
          <td key='fcc-have' className={`commodity-diff ${cn.br}`}  >
            <div className='bubble' style={{ backgroundColor: fcDiffCellColor, color: 'black' }} >
              {fcSumElement}
            </div>
          </td>
          {fcMarketIds.map((marketId, i) => {
            const fcCount = fcCargo[marketId][key];
            const fc = proj.linkedFC.find(fc => fc.marketId.toString() === marketId);
            return <td key={`fcc${marketId}`} className={`commodity-need ${i + 1 < fcMarketIds.length || this.state.hasAssignments ? cn.br : ''}`} style={{ position: 'relative', ...this.getBorderStyleForFC(marketId, key) }}>
              {fc && <InlineMarketOrders fc={fc} cargo={key} count={fcCount} />}
              {fcCargo[marketId][key] ? <span>{fcCount.toLocaleString()}</span> : <span style={{ color: 'grey' }}>-</span>}
            </td>;
          })}
        </>
        }

        {this.state.hasAssignments && <td className='commodity-assigned'><span className='assigned'>{assigned}</span></td>}
      </tr>
    };
  }

  getBorderStyleForFC(marketId: string, cargo: string) {
    if (this.state.proj?.buildId !== 'f2676464-ed64-4b8d-a173-6f5fb0f63e20') { return ''; }

    const bigMap: Record<string, string[]> = {
      '3702255104': ['medicaldiagnosticequipment', 'steel'], // BHV-1KY - Moon Bear
      '3702409984': ['steel', 'titanium', 'nonlethalweapons'], // BHV-1HZ - Star Bear
      '3703929856': ['water', 'liquidoxygen', 'foodcartridges'], // W2T-4KL - Space Bear 
      '3704556800': ['titanium'], // BBV-G5F - C.N.V. Neptuni Ira
      '3704743936': ['ceramiccomposites', 'cmmcomposite', 'fruitandvegetables', 'water'], // WHY-68G - Teddy Bear
      '3704752640': ['cmmcomposite', 'semiconductors'], // B0V-B4G - Grizzly Bear
      '3712755712': ['cmmcomposite', 'insulatingmembrane', 'superconductors'], // B2Y-39J - Drop Bear
      '3714102272': ['aluminium', 'insulatingmembrane', 'polymers', 'powergenerators'], // B6W-34K - Panda Bear
      '3714102528': ['aluminium', 'waterpurifiers'], // B6W-4HB - Polar Bear
      '3714102784': ['aluminium', 'autofabricators', 'copper', 'steel'], // B6W-31J - Sun Bear
      '3714103040': ['steel', 'computercomponents'], // B6W-30X - Spectacled Bear
      '3714464512': ['titanium'], // BZT-5QN - C.N.V. Ticonderoga
    }

    if (bigMap[marketId]?.includes(cargo)) {
      return { border: '1px solid ' + appTheme.palette.themeDarker };
    }

    return '';
  }

  renderProjectDetails(proj: Project) {
    const { isPrep } = this.state;

    return <div className='half'>
      <div className='project'>
        <h3 className={cn.h3}>Project:</h3>
        <table style={{ fontSize: 14 }}>
          <tbody>
            <tr>
              <td>Build name:</td>
              <td>
                <div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight, minWidth: 280 }}>
                  {proj.buildName}
                  {proj.isPrimaryPort && <Icon iconName='CrownSolid' style={{ marginLeft: 8, fontWeight: 'bold' }} title='System primary port' />}
                </div>
              </td>
            </tr>

            {!isPrep && <tr>
              <td>Build type:</td>
              <td><div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}><BuildTypeDisplay buildType={proj.buildType} /></div>
              </td>
            </tr>}

            {!isPrep && <tr>
              <td>System name:</td>
              <td><div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{proj.systemName}</div></td>
            </tr>}

            {!isPrep && !!proj.bodyName && <tr>
              <td>Body name:</td>
              <td><div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{proj.bodyName}&nbsp;</div></td>
            </tr>}

            <tr>
              <td>Architect:</td>
              <td><div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{proj.architectName}&nbsp;</div></td>
            </tr>

            {/* <tr>
              <td>Faction:</td>
              <td><div className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{proj.factionName}&nbsp;</div></td>
            </tr> */}

            {proj.timeDue && !proj.complete && <tr>
              <td>Time remaining:</td>
              <td>
                <div id='due-time' className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>
                  <TimeRemaining timeDue={proj.timeDue} />
                </div>
              </td>
            </tr>}

            {!!proj.notes && <tr>
              <td>Notes:</td>
              <td><div className='grey notes' style={{ backgroundColor: appTheme.palette.purpleLight, maxWidth: 400 }}>{proj.notes}&nbsp;</div></td>
            </tr>}

          </tbody>
        </table>

        {isPrep && this.renderPrepBuilds()}
        {this.renderCommanders()}
        {this.renderLinkedFC()}
        {!this.state.isPrep && !proj.complete && <BuildEffects buildType={proj.buildType} />}
      </div>
    </div>;
  };

  renderPrepBuilds() {
    const { prepBuilds, proj, savingPrepBuilds, prepBuildsDiffer, originalCargo } = this.state;
    if (!prepBuilds || !proj) { return; }

    const rows = Object.entries(prepBuilds).map(([buildType, count]) => {
      const type = getSiteType(buildType)!;
      return <div key={`pbb-${buildType}`} style={{ width: 'max-content' }}>
        <Stack horizontal verticalAlign='center'>
          <div>{count} x {type.displayName2} : {buildType}</div>
          <IconButton
            className={`${cn.bBox}`}
            iconProps={{ iconName: 'BoxAdditionSolid' }}
            title={`Add another ${buildType}`}
            style={{ marginLeft: 8, color: appTheme.palette.themePrimary, width: 22, height: 22 }}
            onClick={() => { this.adjustPrepBuilds(buildType); }}
          />
          <IconButton
            className={`${cn.bBox}`}
            iconProps={{ iconName: count > 1 ? 'BoxSubtractSolid' : 'BoxMultiplySolid' }}
            title={`Remove a ${buildType}`}
            style={{ marginLeft: 4, color: appTheme.palette.themePrimary, width: 22, height: 22 }}
            onClick={() => { this.adjustPrepBuilds(buildType, true); }}
          />
          {buildType.endsWith(' (primary)') && <IconButton
            className={`${cn.bBox}`}
            iconProps={{ iconName: 'Crown' }}
            title={`Use non-primary port costs`}
            style={{ marginLeft: 4, color: appTheme.palette.themePrimary, width: 22, height: 22 }}
            onClick={() => {
              this.adjustPrepBuilds(buildType, true);
              setTimeout(() => this.adjustPrepBuilds(buildType.replace(' (primary)', '')), 10);
            }}
          />}
        </Stack>
      </div>;
    });

    const noBuilds = Object.keys(prepBuilds).length === 0;
    const isDirty = JSON.stringify(originalCargo) !== JSON.stringify(proj.commodities);

    if (!rows.length) {
      rows.push(<div key='pbb-none' style={{ color: appTheme.palette.themeTertiary }}>None</div>);
    }

    return <div style={{ userSelect: 'none' }}>
      <h3 className={cn.h3} style={{ marginTop: 20 }}>

        <Stack horizontal verticalAlign='center'>
          <span>Building:&nbsp;</span>

          {!savingPrepBuilds && <span>
            <ViewEditBuildType
              asAddBtn
              highlightAdd={noBuilds}
              buildType=''
              onChange={bt => {
                if (usePrimaryCosts.includes(bt)) { bt = `${bt} (primary)`; }
                this.adjustPrepBuilds(bt);
              }}
            />
          </span>}

          {isDirty && !savingPrepBuilds && <ActionButton
            className={cn.bBox}
            iconProps={{ iconName: 'Save' }}
            text='Save'
            title='Save changes to buildings'
            style={{ height: 22, color: savingPrepBuilds ? 'grey' : undefined, }}
            disabled={savingPrepBuilds}
            onClick={this.onSavePrepBuilds}
          />}

          {isDirty && !savingPrepBuilds && <ActionButton
            className={cn.bBox}
            iconProps={{ iconName: 'Undo' }}
            text='Undo'
            title='Undo changes to buildings'
            style={{ height: 22, color: savingPrepBuilds ? 'grey' : undefined, }}
            disabled={savingPrepBuilds}
            onClick={() => {
              this.setState({
                prepBuilds: proj.prepBuilds ?? {},
                proj: { ...proj, commodities: originalCargo ?? {} }
              });
            }}
          />}

          {savingPrepBuilds && isDirty && <Spinner
            style={{ marginTop: -2 }}
            size={SpinnerSize.small}
            labelPosition='right'
            label='Saving...'
          />}
        </Stack>
      </h3>

      {prepBuildsDiffer && <Stack horizontal verticalAlign='center' tokens={{ childrenGap: 4 }} style={{ color: appTheme.palette.yellowDark, fontSize: 12, marginBottom: 4 }}>
        <Icon iconName='Warning' />
        <span>Commodities have been manually adjusted</span>
        <Icon iconName='Warning' />
      </Stack>}

      <div style={{ fontSize: 14, marginLeft: 20 }}>
        {rows}
      </div>
    </div>;
  }

  adjustPrepBuilds(newBuild?: string, remove?: boolean) {

    const newPrepBuilds = { ...this.state.prepBuilds };

    if (newBuild) {
      newPrepBuilds[newBuild] = (newPrepBuilds[newBuild] ?? 0) + (remove ? -1 : +1);
      if (newPrepBuilds[newBuild] === 0) { delete newPrepBuilds[newBuild]; }
    }

    // alpha sort + collect total cargo needs
    const cargos: Cargo[] = [];
    const sorted = Object.keys(newPrepBuilds)
      .sort((a, b) => a.localeCompare(b))
      .reduce((m, bt) => {
        m[bt] = newPrepBuilds[bt];
        // sum cargo needs
        const haul = getAvgHaulCosts(bt);
        for (let n = 0; n < m[bt]; n++) {
          cargos.push(haul);
        }

        return m;
      }, {} as Record<string, number>)

    // determine new cargo needs
    const cargoNeeded = mergeCargo(cargos);

    const newProj: Project = {
      ...this.state.proj!,
      commodities: cargoNeeded,
      maxNeed: sumCargo(cargoNeeded),
    };

    this.setState({
      prepBuilds: sorted,
      proj: newProj,
      sumTotal: sumCargo(cargoNeeded),
    });
  }

  calcCargoNeedsForPrepBuilds(prepBuilds: Record<string, number>) {
    // alpha sort + collect total cargo needs
    const cargos: Cargo[] = [];
    Object.keys(prepBuilds)
      .reduce((m, bt) => {
        m[bt] = prepBuilds[bt];
        // sum cargo needs
        const haul = getAvgHaulCosts(bt);
        for (let n = 0; n < m[bt]; n++) {
          cargos.push(haul);
        }

        return m;
      }, {} as Record<string, number>)

    // determine new cargo needs
    const cargoNeeded = mergeCargo(cargos);
    return cargoNeeded;
  }

  onSavePrepBuilds = async () => {
    const { proj, prepBuilds } = this.state;
    if (!proj?.buildId || !prepBuilds) return;

    this.setState({ savingPrepBuilds: true });
    await delay(500);

    try {
      const deltaProj = {
        buildId: proj.buildId,
        prepBuilds: prepBuilds,
      };
      const savedProj = await api.project.update(proj.buildId, deltaProj);

      // success
      const prepBuildsDiffer = this.state.isPrep && savedProj.prepBuilds && sumCargo(savedProj.commodities) !== sumCargo(this.calcCargoNeedsForPrepBuilds(savedProj.prepBuilds));
      this.setState({
        savingPrepBuilds: false,
        proj: savedProj,
        originalCargo: { ...savedProj.commodities },
        lastTimestamp: savedProj.timestamp,
        editReady: new Set(savedProj.ready),
        sumTotal: sumCargo(savedProj.commodities),
        hasAssignments: Object.keys(savedProj.commanders).reduce((s, c) => s += savedProj.commanders[c].length, 0) > 0,
        prepBuilds: savedProj.prepBuilds,
        prepBuildsDiffer,
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  renderCommanders() {
    const { proj, showAddCmdr, newCmdr, isPrep } = this.state;
    if (!proj?.commanders) { return <div />; }

    const rows = [];
    for (const key in proj.commanders) {
      const hideCmdrRemove = isPrep && isMatchingCmdr(key, proj.architectName);
      var row = <li key={`@${key}`}>
        <span className={`removable ${cn.removable}`}>
          {key}
          {!hideCmdrRemove && <Icon
            className={`btn ${cn.btn}`}
            iconName='Delete'
            title={`Remove commander: ${key}`}
            style={{ color: appTheme.palette.themePrimary, }}
            onClick={() => { this.onClickCmdrRemove(key); }}
          />}
        </span>
      </li>;
      rows.push(row)
    }

    return <>
      <h3 className={cn.h3} style={{ marginTop: 20 }}>
        {Object.keys(proj.commanders).length ?? 0} Commanders:
        &nbsp;
        {!showAddCmdr && <ActionButton
          className={cn.bBox}
          iconProps={{ iconName: 'Add' }}
          text='Add'
          title='Add a new Commander to this project'
          style={{ marginLeft: 10, padding: 0, height: 22, }}
          onClick={this.onShowAddCmdr}
        />}
      </h3>

      {showAddCmdr && <div className='add-cmdr'>
        <Stack horizontal tokens={{ childrenGap: 4, padding: 4, }}>
          <TextField
            id='new-cmdr-edit'
            name='cmdr'
            value={newCmdr}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter') { this.onClickAddCmdr(); }
              if (ev.key === 'Escape') { this.setState({ showAddCmdr: false }); }
            }}
            onChange={(_, v) => this.setState({ newCmdr: v })}
          />
          <PrimaryButton text='Add' onClick={this.onClickAddCmdr} iconProps={{ iconName: 'Add' }} />
          <IconButton title='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => this.setState({ showAddCmdr: false })} />
        </Stack>
      </div>}

      <ul style={{ fontSize: 14 }}>
        {rows}
      </ul>
    </>
  }

  renderLinkedFC() {
    const { proj, showAddFC, savingFC, fcEditMarketId } = this.state;
    if (!proj) { return <div />; }

    const rows = proj.linkedFC.map(item => (<li key={`@${item.marketId}`}>
      <span className={`removable ${cn.removable}`}>
        {fcFullName(item.name, item.displayName)}
        &nbsp;
        <Icon
          className={`btn ${cn.btn}`}
          iconName='Edit'
          title={`Edit FC: ${item.displayName} (${item.name})`}
          style={{ color: appTheme.palette.themePrimary }}
          onClick={() => {
            this.setState({ fcEditMarketId: item.marketId.toString() });
          }}
        />
        &nbsp;
        <Icon
          className={`btn ${cn.btn}`}
          iconName='Delete'
          title={`Unlink FC: ${item.displayName} (${item.name})`}
          style={{ color: appTheme.palette.themePrimary }}
          onClick={() => { this.onClickUnlinkFC(proj.buildId, item.marketId); }}
        />
      </span>
    </li>));

    let preMatches: Record<string, string> | undefined = undefined;
    if (showAddFC) {
      const cmdrLinkedFCs = store.cmdrLinkedFCs;
      preMatches = Object.keys(store.cmdrLinkedFCs)
        .filter(marketId => proj.linkedFC.every(fc => fc.marketId.toString() !== marketId))
        .reduce((map, marketId) => {
          map[marketId] = cmdrLinkedFCs[marketId];
          return map;
        }, {} as Record<string, string>);
    }

    const linkedMarketIds = this.state.proj?.linkedFC.map(fc => fc.marketId.toString()) ?? [];
    return <>
      <h3 className={cn.h3}>
        {proj.linkedFC.length ?? 0} Linked Fleet Carriers:
        &nbsp;
        {!showAddFC && <ActionButton
          className={cn.bBox}
          iconProps={{ iconName: 'Add' }}
          text='Add'
          title='Link a new Fleet Carrier to this project'
          style={{ marginLeft: 10, padding: 0, height: 22, }}
          onClick={() => {
            this.setState({
              showAddFC: true,
              showAddCmdr: false,
            });
            delayFocus('add-fc-combo-input');
          }}
        />}
      </h3>
      {showAddFC && <div>
        <Label>Enter Fleet Carrier name:</Label>
        <FindFC
          preMatches={preMatches}
          notThese={linkedMarketIds}
          processing={savingFC}
          onChange={(marketId) => {
            if (marketId) {
              this.onClickLinkFC(marketId)
            } else {
              this.setState({ showAddFC: false });
            }
          }}
        />

      </div>}

      <ul style={{ fontSize: 14 }}>
        {rows}
      </ul>

      {fcEditMarketId && <FleetCarrier
        marketId={fcEditMarketId}
        onClose={() => {
          this.setState({ fcEditMarketId: undefined });
          this.fetchCargoFC(this.state.proj!.buildId);
        }}
      />}
    </>
  }

  onClickCmdrRemove = async (cmdr: string) => {
    if (!this.state.proj?.buildId || !cmdr) { return; }

    try {
      await api.project.unlinkCmdr(this.state.proj.buildId, cmdr);

      // success - remove from in-memory data
      const { proj } = this.state;
      if (!proj.commanders) { proj.commanders = {}; }
      delete proj.commanders[cmdr];
      this.setState({ proj });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onShowAddCmdr = () => {
    console.log('onShowAddCmdr:', this.state.showAddCmdr);
    const cmdrName = store.cmdrName;
    const cmdrKnown = cmdrName && this.state.proj && Object.keys(this.state.proj?.commanders).some(cmdr => cmdr.toLowerCase() === cmdrName.toLowerCase());
    if (!cmdrKnown) {
      this.setState({ newCmdr: cmdrName })
    }
    this.setState({ showAddCmdr: true, showAddFC: false, })
    delayFocus('new-cmdr-edit');
  }

  onClickAddCmdr = async () => {
    if (!this.state.proj?.buildId || !this.state.newCmdr) {
      this.setState({
        showAddCmdr: false,
        newCmdr: '',
      });
      return;
    }
    try {
      const { newCmdr } = this.state;
      await api.project.linkCmdr(this.state.proj.buildId, newCmdr.trim());

      // success - add to in-memory data
      const { proj } = this.state;
      if (!proj.commanders) { proj.commanders = {}; }
      proj.commanders[newCmdr] = [];
      this.setState({
        showAddCmdr: false,
        newCmdr: '',
        proj
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onClickLinkFC = async (marketId: string) => {
    if (this.state.buildId && marketId) {
      try {
        this.setState({ savingFC: true });
        const linkedFCs = await api.project.linkFC(this.state.buildId, marketId);
        this.updateLinkedFC(this.state.buildId, linkedFCs);
      } catch (err: any) {
        this.setState({ errorMsg: err.message, savingFC: false });
      }
    }
  }

  onClickUnlinkFC = async (buildId: string, marketId: number) => {
    try {
      // remove cargo counts before making the API call
      const fcCargoTemp = { ...this.state.fcCargo };
      delete fcCargoTemp[marketId];
      this.setState({ fcCargo: fcCargoTemp });

      const linkedFCs = await api.project.unlinkFC(buildId, marketId);

      this.updateLinkedFC(buildId, linkedFCs);

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  updateLinkedFC(buildId: string, linkedFCs: ProjectFC[]) {
    const updatedProj = {
      ...this.state.proj!,
      linkedFC: linkedFCs,
    };
    this.setState({
      proj: updatedProj,
      originalCargo: { ...updatedProj.commodities },
      lastTimestamp: updatedProj.timestamp,
      showAddFC: false,
      savingFC: false,
      deliverMarketId: linkedFCs.find(fc => fc.marketId.toString() === this.state.deliverMarketId)?.marketId.toString() ?? 'site',
      errorMsg: undefined,
    });
    this.fetchCargoFC(buildId);
  }

  onClickAssign = async (assignCmdr: string) => {
    const { proj, assignCommodity } = this.state;
    if (!proj?.buildId || !assignCmdr || !assignCommodity) { return; }
    try {
      await api.project.assignCmdr(proj.buildId, assignCmdr, assignCommodity);

      // success - add to in-memory data
      if (!proj.commanders) { proj.commanders = {}; }
      if (!proj.commanders[assignCmdr]) { proj.commanders[assignCmdr] = []; }

      proj.commanders[assignCmdr].push(assignCommodity)
      this.setState({
        assignCommodity: undefined,
        hasAssignments: Object.keys(proj.commanders).reduce((s, c) => s += proj.commanders[c].length, 0) > 0,
        proj
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onClickUnassign = async (cmdr: string, commodity: string) => {
    const { proj } = this.state;
    if (!proj || !proj.buildId || !proj.commanders || !cmdr || !commodity || !proj.commanders[cmdr]) { return; }
    try {
      await api.project.unAssignCmdr(proj.buildId, cmdr, commodity);

      // success - remove from to in-memory data
      const idx = proj.commanders[cmdr].indexOf(commodity)
      proj.commanders[cmdr].splice(idx, 1);
      this.setState({
        proj,
        hasAssignments: Object.keys(proj.commanders).reduce((s, c) => s += proj.commanders[c].length, 0) > 0,
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onToggleReady = async (buildId: string, commodity: string, isReady: boolean) => {
    try {
      await api.project.setReady(buildId, [commodity], isReady);

      // success - remove from in-memory data
      const editReady = this.state.editReady;
      if (isReady)
        editReady.delete(commodity)
      else
        editReady.add(commodity)
      this.setState({ editReady });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onProjectDelete = async () => {
    const buildId = this.state.proj?.buildId;
    if (!buildId) return;

    // add a little artificial delay so the spinner doesn't flicker in and out
    this.setState({ submitting: true });
    await delay(500);

    try {
      await api.project.delete(buildId);

      // success - navigate to home
      store.removeRecentProject(buildId);
      const nextUrl = this.state.proj?.systemName
        ? `#sys=${encodeURIComponent(this.state.proj?.systemName)}`
        : `#build`;
      window.location.assign(nextUrl);
      window.location.reload();

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onProjectComplete = async () => {
    const buildId = this.state.proj?.buildId;
    if (!buildId) return;

    // add a little artificial delay so the spinner doesn't flicker in and out
    this.setState({ submitting: true });
    await delay(500);

    try {
      await api.project.complete(buildId);

      window.location.reload();

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  onUpdateProjectCommodities = async () => {
    const { proj, editCommodities } = this.state;

    if (!!proj?.commodities && proj.buildId && editCommodities) {
      const deltaProj = {
        buildId: proj.buildId,
        commodities: {} as Cargo
      };

      // only send deltas
      for (const key in editCommodities) {
        if (proj.commodities[key] !== editCommodities[key]) {
          deltaProj.commodities[key] = editCommodities[key];
        }
      }
      // delete anything that was removed?
      for (const key in proj.commodities) {
        if (!!proj.commodities[key] && !editCommodities[key]) {
          deltaProj.commodities[key] = -1;
        }
      }

      // stop here if nothing changed
      if (Object.keys(deltaProj.commodities).length === 0) {
        this.setState({
          mode: Mode.view,
          editCommodities: undefined,
        });
        return;
      }

      // add a little artificial delay so the spinner doesn't flicker in and out
      this.setState({ submitting: true });
      await delay(500);

      try {
        const savedProj = await api.project.update(proj.buildId, deltaProj);
        const prepBuildsDiffer = this.state.isPrep && savedProj.prepBuilds && sumCargo(savedProj.commodities) !== sumCargo(this.calcCargoNeedsForPrepBuilds(savedProj.prepBuilds));

        // success - apply new commodity count
        this.setState({
          proj: savedProj,
          originalCargo: { ...savedProj.commodities },
          lastTimestamp: savedProj.timestamp,
          editReady: new Set(savedProj.ready),
          sumTotal: Object.values(savedProj.commodities).reduce((total, current) => total += current, 0),
          hasAssignments: Object.keys(savedProj.commanders).reduce((s, c) => s += savedProj.commanders[c].length, 0) > 0,
          editCommodities: undefined,
          mode: Mode.view,
          submitting: false,
          prepBuildsDiffer,
        });

      } catch (err: any) {
        this.setState({ errorMsg: err.message });
      }
    }
  };

  setDefaultApproxCargoCounts = async () => {
    try {
      // add a little artificial delay so the spinner doesn't flicker in and out
      this.setState({ submitting: true });
      await delay(500);

      const savedProj = await api.project.setDefaultCargo(this.state.proj?.buildId!)
      // success - apply new commodity count
      this.setState({
        proj: savedProj,
        originalCargo: { ...savedProj.commodities },
        lastTimestamp: savedProj.timestamp,
        editReady: new Set(savedProj.ready),
        sumTotal: Object.values(savedProj.commodities).reduce((total, current) => total += current, 0),
        hasAssignments: Object.keys(savedProj.commanders).reduce((s, c) => s += savedProj.commanders[c].length, 0) > 0,
        editCommodities: undefined,
        mode: Mode.view,
        submitting: false,
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  }

  renderMissingMarketId() {
    const { fixMarketId } = this.state;

    return <>
      <MessageBar
        messageBarType={MessageBarType.warning}
        actions={<MessageBarButton onClick={() => this.setState({ fixMarketId: '' })}>Fix it</MessageBarButton>}
      >
        Unfortunately this build project was created without a key piece of information. You can continue to use it as is but the SrvSurvey app cannot auto update commodities needed until this is corrected.
      </MessageBar>
      <Modal isOpen={fixMarketId !== undefined} styles={{ main: { border: '1px solid ' + appTheme.palette.themePrimary, } }}>
        <div style={{ textAlign: 'left' }}>
          <h3 className={cn.h3}>Set corrected Market ID</h3>
          <div>The <code className={cn.navy}>MarketID</code> value can be found in your journal files once docked at the construction ship or site for this project:</div>
          <ul>
            <li>Open folder: <code className={cn.navy}>%HomeDrive%%HomePath%\Saved Games\Frontier Developments\Elite Dangerous</code></li>
            <li>Find the file named with today's date. Something like: <code className={cn.navy}>Journal.{new Date().toISOString().substring(0, 10)}T102030.01.log</code></li>
            <li>Scroll to the bottom and look for the line with <code className={cn.navy}>"event":"Docked"</code></li>
            <li>On that line, copy the value of <code className={cn.navy}>MarketID</code> and paste it here</li>
          </ul>

          <Stack horizontal tokens={{ childrenGap: 10, padding: 10, }}>
            <TextField
              name='marketId'
              value={fixMarketId}
              onChange={(_, v) => {
                this.setState({ fixMarketId: v ?? '' });
              }}
            />
            <PrimaryButton iconProps={{ iconName: 'Save' }} text='Save' onClick={this.saveNewMarketId} disabled={!(parseInt(this.state.fixMarketId ?? '').toString().trim() === this.state.fixMarketId?.trim())} />
            <DefaultButton iconProps={{ iconName: 'Cancel' }} text='Cancel' onClick={() => this.setState({ fixMarketId: undefined })} />
          </Stack>
        </div>
      </Modal>
    </>;
  }

  saveNewMarketId = async () => {
    const { proj, fixMarketId } = this.state;
    if (!proj?.buildId || !fixMarketId) return;

    try {
      const deltaProj = {
        buildId: proj.buildId,
        marketId: parseInt(fixMarketId),
      };
      const savedProj = await api.project.update(proj.buildId, deltaProj);

      // success
      console.log('saveNewMarketId: savedProj:', savedProj);
      this.setState({
        proj: savedProj,
        originalCargo: { ...savedProj.commodities },
        lastTimestamp: savedProj.timestamp,
        editReady: new Set(savedProj.ready),
        sumTotal: sumCargo(savedProj.commodities),
        hasAssignments: Object.keys(savedProj.commanders).reduce((s, c) => s += savedProj.commanders[c].length, 0) > 0,
        fixMarketId: undefined,
      });

    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  };

  renderStats() {
    const { summary, proj, sumTotal, ships } = this.state;
    if (!proj) { return; }
    // roughly calculate progress by the curremt sum from the highest value known
    const approxProgress = proj.maxNeed - sumTotal;
    let percent = 100 / proj.maxNeed * approxProgress;

    const shipsCargo = mergeCargo((ships ?? []).map(s => s.cargo).flat());
    const shipsTotal = getCargoCountOnHand(proj.commodities, shipsCargo);

    // TODO: unify "amount delivered" across deliveries and amount remaining

    if (!summary) {
      // for fc_loading projects...
      percent = 100 / proj.maxNeed * this.countReadyOnFCs;
      return <div className='half'>
        <h3 className={cn.h3}>Progress: {Math.floor(percent)}%</h3>
        <div className='stats-header'>
          <>
            Ready on Fleet Carriers: <span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{this.countReadyOnFCs.toLocaleString()}</span>
            {!!shipsTotal && <> and on ships: <span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{shipsTotal.toLocaleString()}</span></>}
          </>
        </div>
        {(approxProgress > 0 || this.countReadyOnFCs > 0) && <ChartGeneralProgress noProgress progress={approxProgress} onShips={shipsTotal} readyOnFC={this.countReadyOnFCs} maxNeed={proj.maxNeed} />}
      </div>;
    }

    const cmdrColors = getColorTable(Object.keys(summary.cmdrs));
    return <div className='half'>
      <h3 className={cn.h3}>Progress: {Math.floor(percent)}%</h3>
      <div style={{ fontSize: 14 }}>
        {!!summary.totalDeliveries && <div className='stats-header'>
          <span>Total cargo delivered: </span><span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{approxProgress.toLocaleString()}</span>
          <span>&nbsp;Deliveries tracked:&nbsp;</span><span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{summary.totalDeliveries.toLocaleString()}</span>
          <div>
            Ready on Fleet Carriers: <span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{this.countReadyOnFCs.toLocaleString()}</span>
            {!!shipsTotal && <> and on ships: <span className='grey' style={{ backgroundColor: appTheme.palette.purpleLight }}>{shipsTotal.toLocaleString()}</span></>}
          </div>
        </div>}

        {(approxProgress > 0 || this.countReadyOnFCs > 0) && <ChartGeneralProgress progress={approxProgress} onShips={shipsTotal} readyOnFC={this.countReadyOnFCs} maxNeed={proj.maxNeed} />}

        <ChartByCmdrs summary={summary} cmdrColors={cmdrColors} />

        {!!summary.totalDeliveries && <>
          <div className='stats-cmdr-over-time'>Cargo deliveries per hour:</div>
          <ChartByCmdrsOverTime summary={summary} complete={proj.complete} />
        </>}

        {!summary.totalDeliveries && <div>
          <br />
          No tracked deliveries yet.
          <br />
          Use <LinkSrvSurvey /> to track deliveries automatically.
        </div>}
      </div>
    </div>;
  }

  renderDeliver() {
    const { proj, sort, nextDelivery, showBubble, submitting, fcCargo, deliverMarketId, isPrep } = this.state;
    if (!proj) return;

    // build up delivery options if there are FCs we can deliver to
    const fcCargoKeys = Object.keys(fcCargo);
    const destinationOptions: IDropdownOption[] = [
      { key: 'site', text: 'Construction site', data: { icon: 'Manufacturing' } },
      { key: 'divider_1', text: '-', itemType: DropdownMenuItemType.Divider },
      ...fcCargoKeys.map(marketId => ({ key: marketId, text: this.getFullFCName(marketId) }))
    ];
    if (isPrep) {
      // remove construction site if isPrep
      destinationOptions.splice(0, 1);
    }

    const destinationPicker = <Dropdown
      id='deliver-destination'
      openOnKeyboardFocus
      placeholder='Choose a destination...'
      style={{ width: 200 }}
      options={destinationOptions}
      onRenderOption={(o) => {
        return <>
          <Icon className='icon-inline' iconName={o?.key === 'site' ? 'Manufacturing' : 'fleetCarrierBlack'} />
          &nbsp;{o?.text}
        </>;
      }}
      dropdownWidth='auto'
      selectedKey={deliverMarketId}
      onChange={(_, o) => this.setState({ deliverMarketId: o!.key.toString() })}
      styles={{ callout: { border: '1px solid ' + appTheme.palette.themePrimary } }}
    />;

    // valid cargo names that can be included on a delivery
    const validCargoKeys = Object.keys(proj.commodities)
      .filter(k => proj.commodities[k] > 0)

    const disableDeliver = submitting || sumCargo(nextDelivery) === 0 || (isPrep && (!deliverMarketId || deliverMarketId === 'site'));
    return <div className='delivery'>
      {!submitting && <Stack horizontal tokens={{ childrenGap: 10, padding: 10, }}>
        <PrimaryButton
          text='Deliver'
          disabled={disableDeliver}
          iconProps={{ iconName: 'DeliveryTruck' }}
          onClick={this.deliverToSite}
        />
        {fcCargoKeys.length > 0 && destinationPicker}
        <DefaultButton text='Cancel' iconProps={{ iconName: 'Cancel' }} onClick={() => {
          this.setState({ mode: Mode.view });
        }} />
      </Stack>}

      {submitting && <Spinner
        className='submitting'
        label="Submitting delivery ..."
        labelPosition="right"
      />}

      <EditCargo
        cargo={nextDelivery}
        readyNames={Array.from(this.state.editReady)}
        maxCounts={proj.commodities}
        validNames={validCargoKeys}
        sort={sort}
        addButtonBelow={true}
        showTotalsRow
        onChange={cargo => this.setState({ nextDelivery: cargo })}
      />

      {showBubble && <TeachingBubble
        target={'#current-cmdr'}
        headline='Who are you?'
        hasCloseButton={true}
        onDismiss={() => { this.setState({ showBubble: false }) }}
      >
        Enter your cmdr's name to get credit for this delivery.
      </TeachingBubble>}

    </div>;
  }

  deliverToZero(commodity: string, count: number) {
    // update local count state before API call
    const updateProj = this.state.proj!;
    updateProj.commodities[commodity] = 0;
    this.setState({ proj: updateProj });

    const cargo: Cargo = {};
    cargo[commodity] = count;
    this.deliverToSite2(
      this.state.proj?.buildId!,
      cargo,
      'site'
    );
  }

  deliverToSite = async () => {
    const { proj, nextDelivery, deliverMarketId } = this.state;
    this.deliverToSite2(proj?.buildId!, nextDelivery, deliverMarketId);
  }

  deliverToSite2 = async (buildId: string, nextDelivery: Cargo, deliverMarketId: string) => {
    try {
      // add a little artificial delay so the spinner doesn't flicker in and out
      this.setState({ submitting: true });
      await delay(500);

      const nextState: Partial<ProjectViewState> = {
        submitting: false,
        mode: Mode.view,
        deliverMarketId: deliverMarketId,
      }
      if (deliverMarketId === 'site') {
        // deliver to the construction site
        const newCommodities = await api.project.deliverToSite(buildId, store.cmdrName ?? 'unknown', nextDelivery);

        // update commodity counts on current project
        const updateProj = this.state.proj!;
        for (const k in newCommodities) {
          updateProj.commodities[k] = newCommodities[k];
        }

        // update state and re-fetch stats
        nextState.proj = updateProj;
        nextState.editReady = new Set(updateProj.ready);
        nextState.sumTotal = Object.values(updateProj.commodities).reduce((total, current) => total += current, 0);
        this.fetchProjectStats(buildId);
      } else {
        // deliver to some FC
        const newCommodities = await api.fc.deliverToFC(deliverMarketId, nextDelivery);

        // update commodity counts on the FC project
        const fcCargoUpdate = this.state.fcCargo;
        const fcCargo = fcCargoUpdate[deliverMarketId];
        for (const k in newCommodities) { fcCargo[k] = newCommodities[k]; }
        nextState.fcCargo = fcCargoUpdate;
      }

      this.setState(nextState as ProjectViewState);
      store.deliver = nextDelivery;
      store.deliverDestination = deliverMarketId;
    } catch (err: any) {
      this.setState({ errorMsg: err.message });
    }
  };

  toggleAutoRefresh = () => {
    if (this.state.autoUpdateUntil > 0) {
      // stop auto-refresh poll and don't refresh at this time.
      console.log(`Stopping timer at: ${new Date().toISOString()}`);
      clearTimeout(this.timer);
      this.setState({ autoUpdateUntil: 0 });
    } else if (this.state.proj?.buildId) {
      // start polling (which causes an immediate refresh)
      console.log(`Starting timer at: ${new Date().toISOString()}`);
      this.setState({ autoUpdateUntil: Date.now() + autoUpdateStopDuration });
      this.fetchProject(this.state.proj?.buildId, true);
    }
  };

  renderSystemEffects(proj: Project) {
    const { sysMap } = this.state;
    const site = sysMap?.siteMaps.find(s => s.buildId === proj.buildId);

    return <div className='half'>
      {site && sysMap && <>
        {site.status === 'complete' && <EconomyTable2 site={site} sysView={undefined!} />}
        <MarketLinks site={site} />
      </>}

      <BuildEffects buildType={proj.buildType} siteMap={site} />
    </div>;
  }

  renderShipsCallout() {
    const { ships, showShipsTargetId, showShipsTargetCargo } = this.state;
    if (!ships) return null;
    const bw = 50;

    const rows = [];
    for (const s of ships) {
      const matchingCargo = Object.keys(s.cargo)
        .filter(c => !showShipsTargetCargo || showShipsTargetCargo === c)
        .sort();
      const sum = matchingCargo.reduce((t, c) => t + s.cargo[c], 0);
      if (sum === 0) { continue; }
      const sumAll = Object.values(s.cargo).reduce((t, c) => t + c, 0);

      const w = 100.0 / s.maxCargo * sumAll;
      // elements for the cmdr
      rows.push(
        <div key={`ship-${s.cmdr}-1`} style={{ marginBottom: 2 }}>{s.cmdr}</div>,
        <div key={`ship-${s.cmdr}-2`} style={{ marginBottom: 2, color: appTheme.palette.themePrimary }}>{mapShipNames[s.type] ?? s.type}</div>,
        <div key={`ship-${s.cmdr}-3`} style={{ marginBottom: 2, gridColumn: 'span 2', fontSize: 10, position: 'relative', minWidth: bw, height: 19 }}>
          <div style={{ textAlign: 'center' }}>{sum} of {s.maxCargo.toLocaleString()}</div>
          <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${w}%`, height: 4, backgroundColor: appTheme.palette.themePrimary }}></div>
          <div style={{ position: 'absolute', bottom: 0, left: `${w}%`, width: `${100 - w}%`, height: 4, backgroundColor: appTheme.palette.neutralQuaternaryAlt }}></div>
          {!!showShipsTargetCargo && <div style={{ position: 'absolute', bottom: 0, left: 0, width: `${100.0 / s.maxCargo * sum}%`, height: 4, backgroundColor: appTheme.palette.orangeLight }}></div>}
        </div>,
        <div key={`ship-${s.cmdr}-cargo-${s.cmdr}-4`} style={{ gridRow: `span ${matchingCargo.length}`, fontSize: 10, color: appTheme.palette.themePrimary }}>{getRelativeDuration(new Date(s.time))}</div>,
      );

      // elements for the cargo
      let flip = true;
      const fontSize = 11;
      for (const key of matchingCargo) {
        flip = !flip;
        const backgroundColor = flip ? appTheme.palette.neutralQuaternaryAlt : undefined
        rows.push(
          <div key={`ship-${s.cmdr}-cargo-${key}-5`} style={{ backgroundColor, gridColumn: 'span 1', textAlign: 'right', fontSize, paddingLeft: 10, marginRight: -10 }}>{mapCommodityNames[key] ?? key}</div>,
          <div key={`ship-${s.cmdr}-cargo-${key}-6`} style={{ backgroundColor, gridColumn: 'span 1', textAlign: 'right', fontSize, marginRight: 0, paddingRight: 10 }}>{s.cargo[key].toLocaleString()}</div>,
          <div key={`ship-${s.cmdr}-cargo-${key}-7`} />
        );
      }
      rows.push(<div key={`ship-${s.cmdr}-cargo-${s.cmdr}-8`} style={{ gridColumn: 'span 4', height: 1, margin: '4px 0', backgroundColor: appTheme.palette.themeTertiary }} />);
    }

    return <>
      <Callout
        target={`#${showShipsTargetId}`}
        setInitialFocus
        alignTargetEdge
        directionalHint={DirectionalHint.rightTopEdge}
        styles={{
          beak: { backgroundColor: appTheme.palette.neutralTertiaryAlt, },
          calloutMain: {
            backgroundColor: appTheme.palette.neutralTertiaryAlt,
            color: appTheme.palette.neutralDark,
            cursor: 'default',
          }
        }}
        onDismiss={() => this.setState({ showShipsTargetCargo: undefined, showShipsTargetId: undefined })}
      >
        <div style={{ marginBottom: 10, color: appTheme.palette.themePrimary }}>Commanders:</div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'auto auto auto auto',
          gap: '2px 10px',
          fontSize: '14px',
          marginLeft: 10,
          marginBottom: 10,
          alignItems: 'left',
        }}>
          {rows.slice(0, -1)}
        </div>
      </Callout>
    </>
  }
}

const usePrimaryCosts = [
  'plutus',
  'vulcan',
  'dysnomia',
  'vesta',
  'prometheus',
  'nemesis',
  'no_truss',
  'dual_truss',
  'quad_truss',
  'asteroid',
  'ocellus',
  'apollo',
  'artemis',
  'dodec',
  'quint_truss',
  'dec_truss',
];

